// src/pages/admin/adminpanel/ListingsPanel.jsx
import React, { useState, useEffect } from 'react';

const statusBadge = (status) => ({
  active:  'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  sold:    'bg-blue-100 text-blue-700',
  draft:   'bg-gray-100 text-gray-600',
}[status] || 'bg-gray-100 text-gray-600');

const STATUS_FILTERS = ['all', 'active', 'pending', 'sold', 'draft'];

function usePerPage() {
  const [perPage, setPerPage] = useState(5);
  const change = (n) => setPerPage(n);
  return { perPage, change };
}

function usePagination(data, perPage) {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(data.length / perPage) || 1;
  const slice = data.slice((page - 1) * perPage, page * perPage);
  const reset = () => setPage(1);
  return { page, setPage, totalPages, slice, reset };
}

function PerPageSelector({ perPage, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-secondary">Rows</span>
      <div className="flex gap-1">
        {[5, 10, 20, 50].map(n => (
          <button key={n} onClick={() => onChange(n)}
            className={`w-8 h-7 rounded-lg text-xs font-bold transition-all
              ${perPage === n
                ? 'bg-primary text-white'
                : 'text-secondary hover:bg-surface-container border border-surface-container-high'}`}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function PaginationBar({ page, totalPages, setPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) pages.push(i);
    else if (pages[pages.length - 1] !== '...') pages.push('...');
  }
  return (
    <div className="flex items-center justify-between mt-3">
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:bg-surface-container disabled:opacity-30 transition-all">
        <span className="material-symbols-outlined text-[14px]">chevron_left</span>Prev
      </button>
      <div className="flex items-center gap-1">
        {pages.map((p, i) => p === '...' ? (
          <span key={`d-${i}`} className="px-1 text-xs text-secondary">…</span>
        ) : (
          <button key={p} onClick={() => setPage(p)}
            className={`w-7 h-7 rounded-lg text-xs font-bold transition-all
              ${page === p ? 'bg-primary text-white' : 'text-secondary hover:bg-surface-container'}`}>
            {p}
          </button>
        ))}
      </div>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:bg-surface-container disabled:opacity-30 transition-all">
        Next<span className="material-symbols-outlined text-[14px]">chevron_right</span>
      </button>
    </div>
  );
}

export default function ListingsPanel({ animals, butcherProducts = [], loading }) {
  const [filter, setFilter]   = useState('all');
  const [type,   setType]     = useState('animals'); // 'animals' | 'products'
  const [search, setSearch]   = useState('');

  const SOURCE = type === 'animals' ? animals : butcherProducts;
  const filtered = SOURCE
    .filter(a => filter === 'all' || a.status === filter)
    .filter(a =>
      !search ||
      a.name?.toLowerCase().includes(search.toLowerCase()) ||
      (type === 'animals'
        ? a.seller_name?.toLowerCase().includes(search.toLowerCase())
        : a.butcher_name?.toLowerCase().includes(search.toLowerCase())) ||
      a.category?.toLowerCase().includes(search.toLowerCase())
    );

  const { perPage, change: changePerPage } = usePerPage();
  const pagination = usePagination(filtered, perPage);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { pagination.reset(); }, [filter, type, search]);
  const handlePerPageChange = (n) => { changePerPage(n); pagination.reset(); };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden">

      {/* Header */}
      <div className="p-6 border-b border-surface-container-high
                      flex flex-wrap justify-between items-center gap-4">
        <div>
          <h3 className="font-bold text-lg">All Listings</h3>
          <p className="text-xs text-secondary mt-0.5">
            {filtered.length} of {animals.length} listings
          </p>
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 bg-surface-container-low px-3 py-2
                        rounded-xl border border-outline-variant/20">
          <span className="material-symbols-outlined text-outline text-sm">search</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search animal, seller, category…"
            className="bg-transparent outline-none text-sm w-52
                       placeholder:text-outline-variant"
          />
          {search && (
            <button onClick={() => setSearch('')}
              className="text-outline hover:text-on-surface transition-colors">
              <span className="material-symbols-outlined text-[16px]">close</span>
            </button>
          )}
        </div>
      </div>

      {/* Type pills */}
      <div className="px-6 py-3 flex gap-2 border-b border-surface-container-high bg-surface-container-low/30">
        {[
          { key: 'animals',  label: '🐐 Animals',       count: animals.length        },
          { key: 'products', label: '🥩 Meat Products',  count: butcherProducts.length },
        ].map(t => (
          <button key={t.key} onClick={() => { setType(t.key); setFilter('all'); }}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
              type === t.key
                ? 'bg-primary text-white shadow-sm'
                : 'bg-surface-container text-secondary hover:opacity-80'
            }`}>
            {t.label}
            <span className="ml-1 opacity-70">({t.count})</span>
          </button>
        ))}
      </div>

      {/* Status filter pills */}
      <div className="px-6 py-3 flex gap-2 flex-wrap border-b
                      border-surface-container-high bg-surface-container-low/50">
        {STATUS_FILTERS.map((s) => {
          const count = s === 'all'
            ? animals.length
            : animals.filter(a => a.status === s).length;
          return (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-1 rounded-full text-[11px] font-bold transition-all
                          ${filter === s
                            ? 'bg-primary text-white shadow-sm'
                            : `${statusBadge(s === 'all' ? null : s)}
                               ${s === 'all' ? 'bg-gray-100 text-gray-600' : ''}
                               hover:opacity-80`
                          }`}>
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              <span className="ml-1 opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-16 flex justify-center">
          <div className="w-8 h-8 border-4 border-primary
                          border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-secondary">
          <span className="material-symbols-outlined text-4xl text-outline-variant">
            pets
          </span>
          <p className="mt-2 font-semibold">No listings found</p>
          {(search || filter !== 'all') && (
            <p className="text-xs mt-1">Try adjusting your filters</p>
          )}
        </div>
      ) : (
        <>
        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-surface-container">
          {pagination.slice.map((a) => (
            <div key={a.id} className="p-4 flex items-center gap-3">
              {a.image_url
                ? <img src={a.image_url} alt={a.name}
                       className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                : <div className="w-12 h-12 rounded-xl bg-surface-container
                                  flex items-center justify-center flex-shrink-0">
                    <span className="material-symbols-outlined text-outline text-base">pets</span>
                  </div>
              }
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold">{a.name}</p>
                  <span className={`px-2 py-0.5 rounded-full text-[10px]
                                    font-bold ${statusBadge(a.status)}`}>
                    {a.status}
                  </span>
                </div>
                <p className="text-xs text-secondary">
                  {a.category || '—'} · ₹{Number(a.price_inr).toLocaleString('en-IN')}
                </p>
                <p className="text-xs text-secondary">
                  {type === 'animals' ? (a.seller_name || '—') : (a.butcher_name || '—')}
                </p>
                <p className="text-[11px] text-outline">
                  {[a.location_city, a.location_state].filter(Boolean).join(', ') || '—'}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-surface-container-low">
              <tr>
                {['Animal', 'Category', 'Price', 'Seller',
                  'Location', 'Status', 'Listed On'].map(h => (
                  <th key={h}
                    className="px-5 py-3 text-[10px] font-black uppercase
                               tracking-widest text-secondary whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-container">
              {pagination.slice.map((a) => (
                <tr key={a.id}
                  className="hover:bg-surface-container-low/50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      {a.image_url
                        ? <img src={a.image_url} alt={a.name}
                               className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                        : <div className="w-10 h-10 rounded-lg bg-surface-container
                                          flex items-center justify-center flex-shrink-0">
                            <span className="material-symbols-outlined text-outline text-base">pets</span>
                          </div>
                      }
                      <div>
                        <p className="text-sm font-semibold whitespace-nowrap">{a.name}</p>
                        <p className="text-[10px] text-outline font-mono">#{a.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-secondary">{a.category || '—'}</td>
                  <td className="px-5 py-3 text-sm font-bold text-primary">
                    ₹{Number(a.price_inr).toLocaleString('en-IN')}
                  </td>
                  <td className="px-5 py-3 text-sm text-secondary">
                    {type === 'animals' ? (a.seller_name || '—') : (a.butcher_name || '—')}
                  </td>
                  <td className="px-5 py-3 text-sm text-secondary">
                    {[a.location_city, a.location_state].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-[11px]
                                      font-bold ${statusBadge(a.status)}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-secondary whitespace-nowrap">
                    {a.created_at ? new Date(a.created_at).toLocaleDateString('en-IN') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Footer: per-page + count + pagination ── */}
          <div className="px-6 py-4 border-t border-surface-container-high space-y-3">
            <div className="flex items-center justify-between">
              <PerPageSelector perPage={perPage} onChange={handlePerPageChange} />
              <p className="text-xs text-secondary">
                {((pagination.page - 1) * perPage) + 1}–{Math.min(pagination.page * perPage, filtered.length)} of {filtered.length}
              </p>
            </div>
            <PaginationBar
              page={pagination.page}
              totalPages={pagination.totalPages}
              setPage={pagination.setPage}
            />
          </div>
        </>
      )}
    </div>
  );
}